import { useMutation } from '@apollo/client';
import type { AttestationNotRequiredMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { AttestationNotRequiredDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import { useMarkAttestationsNotRequiredTRPC } from './useMarkAttestationsNotRequiredTRPC';

/**
 * Flag-gated wrapper for the `attestationNotRequired` mutation (RSP-6891).
 * Defaults to the legacy Apollo mutation and delegates to the tRPC variant when
 * the `trpc` feature flag is enabled. Consumers import THIS, never the `*TRPC`
 * variant directly.
 */
export const useMarkAttestationsNotRequired = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [markNotRequiredGraphQL, graphqlState] = useMutation(
    AttestationNotRequiredDocument
  );

  const trpcMutation = useMarkAttestationsNotRequiredTRPC();

  const markNotRequired = async (input: {
    Ids: string[];
  }): Promise<AttestationNotRequiredMutation> => {
    if (trpcEnabled) {
      return trpcMutation.markNotRequired(input);
    }

    const result = await markNotRequiredGraphQL({
      variables: { Ids: input.Ids },
    });
    if (!result.data) {
      throw new Error('Failed to mark attestations as not required');
    }

    return result.data;
  };

  return {
    markNotRequired,
    loading: trpcEnabled ? trpcMutation.loading : graphqlState.loading,
    error: trpcEnabled ? trpcMutation.error : graphqlState.error,
  };
};
